import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Plus, Search, Archive, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { fmtDate } from "@/lib/format";
import { PageHeader, Loading, Field } from "@/components/common";
import { DataTable, Btn, TextInput, Select, Textarea } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Modal } from "@/components/Modal";

const empty = { client_name: "", company_name: "", business_unit_ids: [], main_address: "", billing_address: "", primary_contact: "", telephone: "", email: "", account_manager: "", status: "Active", notes: "", financial_visibility: true };

export default function Clients() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [archived, setArchived] = useState(false);
  const [bus, setBus] = useState([]);
  const [buFilter, setBuFilter] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search) p.set("search", search);
      if (archived) p.set("include_archived", "true");
      if (buFilter) p.set("business_unit_id", buFilter);
      const { data } = await api.get(`/clients?${p}`);
      setRows(data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { api.get("/business-units").then((r) => setBus(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, archived, buFilter]);

  const openNew = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setModal(true); };

  const save = async () => {
    if (!form.client_name) return toast.error("Client name is required");
    try {
      if (editing) await api.put(`/clients/${editing.id}`, form);
      else await api.post("/clients", form);
      toast.success("Client saved");
      setModal(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const archive = async (c, restore) => {
    try {
      await api.post(`/clients/${c.id}/${restore ? "restore" : "archive"}`);
      toast.success(restore ? "Client restored" : "Client archived");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const buName = (ids) => (ids || []).map((id) => bus.find((b) => b.id === id)?.name).filter(Boolean).join(", ");

  const columns = [
    { key: "client_name", label: "Client", className: "font-medium text-slate-800", render: (r) => <div><div>{r.client_name}</div><div className="text-xs text-slate-400">{r.company_name}</div></div> },
    { key: "bu", label: "Business Unit", render: (r) => <span className="text-xs">{buName(r.business_unit_ids) || "—"}</span> },
    { key: "account_manager", label: "Account Manager" },
    { key: "site_count", label: "Sites", align: "right", render: (r) => <span className="font-medium">{r.site_count}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "actions", label: "", align: "right", render: (r) => (
      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        {can(user, "edit_clients") && <button onClick={() => openEdit(r)} className="p-1.5 text-slate-400 hover:text-seq-green" data-testid={`edit-client-${r.id}`}><Pencil size={15} /></button>}
        {can(user, "archive_clients") && <button onClick={() => archive(r, r.status === "Archived")} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`archive-client-${r.id}`}><Archive size={15} /></button>}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Clients" subtitle="Manage client accounts, sites and history" icon={Building2}
        actions={can(user, "create_clients") && <Btn onClick={openNew} data-testid="new-client-btn"><Plus size={16} /> New Client</Btn>} />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <TextInput data-testid="client-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients…" className="pl-9" />
        </div>
        <Select value={buFilter} onChange={(e) => setBuFilter(e.target.value)} className="max-w-[180px]">
          <option value="">All business units</option>
          {bus.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
        <label className="flex items-center gap-2 text-sm text-slate-600 ml-auto">
          <input type="checkbox" data-testid="show-archived" checked={archived} onChange={(e) => setArchived(e.target.checked)} className="rounded" /> Show archived
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <Loading /> : <DataTable columns={columns} rows={rows} rowTestId="client-row" onRowClick={(r) => nav(`/clients/${r.id}`)} />}
      </div>

      <Modal open={modal} onOpenChange={setModal} title={editing ? "Edit client" : "New client"} wide testId="client-modal"
        footer={<><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={save} data-testid="save-client-btn">Save</Btn></>}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Client name" required><TextInput data-testid="client-name-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></Field>
          <Field label="Company name"><TextInput value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></Field>
          <Field label="Account manager"><TextInput value={form.account_manager} onChange={(e) => setForm({ ...form, account_manager: e.target.value })} /></Field>
          <Field label="Status"><Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option>Active</option><option>Prospect</option><option>On Hold</option></Select></Field>
          <Field label="Main address" className="sm:col-span-2"><TextInput value={form.main_address} onChange={(e) => setForm({ ...form, main_address: e.target.value })} /></Field>
          <Field label="Billing address" className="sm:col-span-2"><TextInput value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} /></Field>
          <Field label="Telephone"><TextInput value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} /></Field>
          <Field label="Email"><TextInput value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Business units" className="sm:col-span-2">
            <div className="flex flex-wrap gap-2">
              {bus.map((b) => {
                const on = form.business_unit_ids?.includes(b.id);
                return <button type="button" key={b.id} onClick={() => setForm({ ...form, business_unit_ids: on ? form.business_unit_ids.filter((x) => x !== b.id) : [...(form.business_unit_ids || []), b.id] })}
                  className={`px-3 py-1.5 rounded-full text-xs border ${on ? "bg-seq-green text-white border-seq-green" : "bg-white text-slate-600 border-slate-300"}`}>{b.name}</button>;
              })}
            </div>
          </Field>
          <Field label="Notes" className="sm:col-span-2"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
      </Modal>
    </div>
  );
}
